import React, { useEffect, useState } from "react";
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
  Chip,
  Typography,
  Button,
  MenuItem,
} from "@mui/material";
import { Edit, Image } from "@mui/icons-material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import api from "../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Material Dashboard UI Components
import MDInput from "components/MDInput";

const Boletas = () => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [dbUser, setDbUser] = useState("");
  const [boletas, setBoletas] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedBoleta, setSelectedBoleta] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [imageSrc, setImageSrc] = useState("");

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user || null);
      setDbUser(user ? user.displayName || user.uid : "");
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!dbUser) return;
    const nowEpoch = Math.floor(Date.now() / 1000);
    const THIRTY_DAYS = 30 * 24 * 60 * 60;
    api.boletasFecha(dbUser, nowEpoch - (12*THIRTY_DAYS), nowEpoch).then((data) => {
      setBoletas(data);
    });

    // Obtener categorías desde api.colorsMap.keys
    setCategorias(Object.keys(api.colorsMap));
  }, [dbUser]);

  const handleEdit = (boleta) => {
    setSelectedBoleta({ ...boleta });
    setOpenEditModal(true);
  };

  const handleEditChange = (e) => {
    let { name, value } = e.target;

    if (name === "monto_usd") {
      value = parseFloat(value) || 0;
      if (value < 1) value = 1;
      const tasaCambio = selectedBoleta.monto_uyu / selectedBoleta.monto_usd || 1;
      setSelectedBoleta({
        ...selectedBoleta,
        [name]: value,
        monto_uyu: Math.round(value * tasaCambio),
      });
    } else {
      setSelectedBoleta({
        ...selectedBoleta,
        [name]: value,
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedBoleta) return;
    await api.updateBoleta(selectedBoleta);
    setBoletas(boletas.map((b) => (b.id === selectedBoleta.id ? selectedBoleta : b)));
    setOpenEditModal(false);
  };

  const handleViewImage = async (boleta) => {
    try {
      const imageBase64 = await api.getImage(boleta.imagen_id); // Se usa la API para obtener la imagen en Base64
      setImageSrc(imageBase64);
      setOpenImageModal(true);
    } catch (error) {
      console.error("Error al obtener la imagen:", error);
    }
  };

  const columns = [
    { Header: "Fecha", accessor: "fecha", Cell: ({ value }) => (<b>{new Date(value * 1000).toISOString().slice(0, 10)}</b>) },
    { Header: "Monto USD", accessor: "monto_usd" , Cell: ({ value }) => (<b>{value}USD</b>) },
    { Header: "Monto $", accessor: "monto_uyu" , Cell: ({ value }) => (<b>${value}</b>) },
    { Header: "Categoría", accessor: "categoria" ,
        Cell: ({ value }) => (
            <Chip 
              label={value} 
              size="small"
              sx={{
                backgroundColor: api.colorsMap[value] || "#007bff", // Usa el color de la categoría o un azul por defecto
                color: "#fff", // Texto blanco para mejor contraste
                fontWeight: "bold"
              }}
            />
          ) 
    },
    { Header: "Descripción", accessor: "descripcion", Cell: ({ value }) => (value.length > 40 ? value.slice(0, 40) + "..." : value) },
    {
      Header: "Acciones",
      accessor: "acciones",
      Cell: ({ row }) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(row.original)}>
            <Edit />
          </IconButton>
          <IconButton color="secondary" onClick={() => handleViewImage(row.original)}>
            <Image />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom color="primary">
            Boletas
          </Typography>
          <DataTable
            table={{ columns, rows: boletas }}
            isSorted
            entriesPerPage
            showTotalEntries
            canSearch
          />
        </CardContent>
      </Card>

      {/* MODAL DE EDICIÓN */}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        sx={{ "& .MuiPaper-root": { bgcolor: "background.default", color: "white" } }}
      >
        <DialogTitle sx={{ color: "white" }}>Editar Boleta</DialogTitle>
        <DialogContent sx={{ bgcolor: "background.default", color: "white" }}>
          {selectedBoleta && (
            <>
              <MDInput
                select
                fullWidth
                name="categoria"
                value={selectedBoleta?.categoria || ""}
                onChange={handleEditChange}
                variant="outlined"
                label="Categoría"
                sx={{ mb: 2, mt: 2 }}
              >
                {categorias.map((categoria) => (
                  <MenuItem key={categoria} value={categoria}>
                    {categoria}
                  </MenuItem>
                ))}
              </MDInput>

              <MDInput
                label="Descripción"
                name="descripcion"
                value={selectedBoleta?.descripcion || ""}
                onChange={handleEditChange}
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <MDInput
                label="Monto USD"
                name="monto_usd"
                type="number"
                value={selectedBoleta?.monto_usd || ""}
                onChange={handleEditChange}
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <MDInput
                label="Monto $"
                name="monto_uyu"
                type="number"
                value={selectedBoleta?.monto_uyu || ""}
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.default" }}>
          <Button onClick={() => setOpenEditModal(false)} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSaveEdit} color="primary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL PARA VER IMAGEN */}
      <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} 
      sx={{ "& .MuiPaper-root": { bgcolor: "background.default", color: "white" } }}>
        <DialogTitle>Imagen de la Boleta</DialogTitle>
        <DialogContent>
          {imageSrc ? (
            <img src={imageSrc} alt="Boleta" style={{ width: "100%", height: "auto" }} />
          ) : (
            <Typography>No se pudo cargar la imagen.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImageModal(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default Boletas;
